import React, { useEffect, useRef, useState } from 'react'
import { Box, Dialog, IconButton, Typography } from '@material-ui/core'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'
import CloseIcon from '@material-ui/icons/Close'
import clsx from 'clsx'

import {
    useVoiceAgentCallDetail,
    useVoiceAgentLiveCalls,
} from '../../../hooks/useVoiceAgentCalls'
import { VoiceLiveCall } from '../../../models/VoiceAgent'
import { formatE164 } from '../constants'
import { useVoiceAgentStyles } from '../useVoiceAgentStyles'
import { groupSpeakerTurns, parseTranscript } from './parseTranscript'

const callerName = (call: VoiceLiveCall) => call.tenant_name || 'Unknown caller'

const callerNumber = (call: VoiceLiveCall) =>
    call.caller_number ? formatE164(call.caller_number) : ''

const initials = (name: string) => {
    const parts = name.split(' ').filter(Boolean)
    if (!parts.length || name === 'Unknown caller') return '?'
    return (parts[0][0] + (parts[1]?.[0] || '')).toUpperCase()
}

const spokenTurns = (transcript: string) =>
    groupSpeakerTurns(parseTranscript(transcript || ''))

const latestLine = (transcript: string) => {
    const turns = spokenTurns(transcript)
    const last = turns[turns.length - 1]
    if (!last) return 'Waiting for the conversation to start'
    const who = last.speaker === 'ai' ? 'Agent' : 'Caller'
    return `${who}: ${last.text.replace(/\s+/g, ' ')}`
}

const LiveCallBanner = ({
    call,
    onOpen,
}: {
    call: VoiceLiveCall
    onOpen: () => void
}) => {
    const classes = useVoiceAgentStyles()
    const number = callerNumber(call)
    return (
        <button
            type="button"
            className={classes.liveBanner}
            onClick={onOpen}
            aria-label={`View live conversation with ${callerName(call)}`}
        >
            <span
                className={clsx(
                    classes.liveCallDot,
                    call.ended && classes.liveCallDotEnded,
                )}
            />
            <span className={classes.liveBannerBody}>
                <span className={classes.liveBannerTitle}>
                    {call.ended ? 'Call ended' : 'On a call'}
                    <span className={classes.liveBannerCaller}>
                        {callerName(call)}
                        {number ? ` · ${number}` : ''}
                    </span>
                </span>
                <span className={classes.liveBannerPreview}>
                    {latestLine(call.transcript)}
                </span>
            </span>
            <span className={classes.liveBannerAction}>
                View conversation
                <ChevronRightIcon fontSize="small" />
            </span>
        </button>
    )
}

const LiveCallDialog = ({
    call,
    ended,
    onClose,
}: {
    call: VoiceLiveCall
    ended: boolean
    onClose: () => void
}) => {
    const classes = useVoiceAgentStyles()
    const scroller = useRef<HTMLDivElement>(null)
    const { call: detail } = useVoiceAgentCallDetail(
        ended ? String(call.id) : undefined,
        ended,
    )
    const transcript = (ended && detail?.transcript) || call.transcript || ''
    const name = callerName({
        ...call,
        tenant_name: detail?.tenant_name || call.tenant_name,
    })
    const number = callerNumber(call)
    const turns = spokenTurns(transcript)

    useEffect(() => {
        const node = scroller.current
        if (node) node.scrollTop = node.scrollHeight
    }, [transcript, ended])

    return (
        <Dialog
            open
            onClose={onClose}
            maxWidth={false}
            aria-labelledby="live-call-title"
            PaperProps={{ className: classes.liveDialogPaper }}
        >
            <Box className={classes.liveDialogTitle}>
                <Box className={classes.liveDialogAvatar}>{initials(name)}</Box>
                <Box className={classes.liveDialogHeading}>
                    <Box className={classes.liveDialogNameRow}>
                        <Typography
                            id="live-call-title"
                            className={classes.liveDialogName}
                        >
                            {name}
                        </Typography>
                        <span
                            className={clsx(
                                classes.liveStatus,
                                ended
                                    ? classes.liveStatusEnded
                                    : classes.liveStatusOn,
                            )}
                        >
                            {!ended && (
                                <span className={classes.liveStatusDot} />
                            )}
                            {ended ? 'Call ended' : 'Live'}
                        </span>
                    </Box>
                    {number && (
                        <Typography className={classes.liveDialogMeta}>
                            {number}
                        </Typography>
                    )}
                </Box>
                <IconButton aria-label="Close" onClick={onClose} size="small">
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Box>
            {turns.length ? (
                <div ref={scroller} className={classes.liveDialogTranscript}>
                    {turns.map((turn, index) => {
                        const isUser = turn.speaker === 'user'
                        return (
                            <Box
                                key={index}
                                className={clsx(
                                    classes.liveTurn,
                                    isUser
                                        ? classes.liveTurnUser
                                        : classes.liveTurnAi,
                                )}
                            >
                                <Box
                                    className={clsx(
                                        classes.liveSpeaker,
                                        isUser
                                            ? classes.liveSpeakerUser
                                            : classes.liveSpeakerAi,
                                    )}
                                >
                                    {isUser ? initials(name) : 'AI'}
                                </Box>
                                <Box className={classes.liveTurnBody}>
                                    <Typography
                                        className={clsx(
                                            classes.liveTurnLabel,
                                            isUser && classes.liveTurnLabelUser,
                                        )}
                                    >
                                        {isUser ? name : 'Agent'}
                                    </Typography>
                                    <Box
                                        className={clsx(
                                            classes.liveBubble,
                                            isUser
                                                ? classes.liveBubbleUser
                                                : classes.liveBubbleAi,
                                        )}
                                    >
                                        {turn.text}
                                    </Box>
                                </Box>
                            </Box>
                        )
                    })}
                    {ended && (
                        <span className={classes.liveEndedMark}>
                            End of call
                        </span>
                    )}
                </div>
            ) : (
                <Box className={classes.liveDialogTranscript}>
                    <Typography className={classes.liveDialogEmpty}>
                        {ended
                            ? 'This call ended before a transcript was saved.'
                            : 'The transcript will appear as they talk.'}
                    </Typography>
                </Box>
            )}
            <Box className={classes.liveDialogFoot}>
                <span>
                    {ended
                        ? 'The full transcript is saved in the call log.'
                        : 'Updates every few seconds while the call is going.'}
                </span>
            </Box>
        </Dialog>
    )
}

export const LiveCalls = () => {
    const classes = useVoiceAgentStyles()
    const { calls } = useVoiceAgentLiveCalls()
    const [open, setOpen] = useState<{
        call: VoiceLiveCall
        ended: boolean
    } | null>(null)

    useEffect(() => {
        setOpen((current) => {
            if (!current) return current
            const live = calls.find((call) => call.id === current.call.id)
            if (!live) {
                return current.ended
                    ? current
                    : { call: current.call, ended: true }
            }
            const ended = current.ended || live.ended
            if (
                ended === current.ended &&
                live.transcript === current.call.transcript &&
                live.tenant_name === current.call.tenant_name
            ) {
                return current
            }
            return { call: live, ended }
        })
    }, [calls])

    if (!calls.length && !open) return null

    return (
        <>
            {calls.length > 0 && (
                <Box className={classes.liveCalls}>
                    {calls.map((call) => (
                        <LiveCallBanner
                            key={call.id}
                            call={call}
                            onOpen={() => setOpen({ call, ended: false })}
                        />
                    ))}
                </Box>
            )}
            {open && (
                <LiveCallDialog
                    call={open.call}
                    ended={open.ended}
                    onClose={() => setOpen(null)}
                />
            )}
        </>
    )
}
